type LineItem = {
  label: string;
  amount: number;
  meta?: string;
};

type Props = {
  lineItems: LineItem[];
  subtotal: number;
  tax: number;
  total: number;
  deposit: number;
  crewSize: number;
  estimatedHours: number;
  truckSize: string;
  isLongDistance: boolean;
  status: "idle" | "submitting" | "success" | "error";
};

export default function QuoteSummary({
  lineItems,
  subtotal,
  tax,
  total,
  deposit,
  crewSize,
  estimatedHours,
  truckSize,
  isLongDistance,
  status,
}: Props) {
  return (
    <div className="lg:sticky lg:top-24">
      <div className="bg-[#1A1410] text-cream-100 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-5 border-b border-white/10">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-300">
              Your live estimate
            </div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Live
            </div>
          </div>
          <div className="font-display text-5xl md:text-6xl font-bold text-white leading-none tabular-nums">
            ${total.toLocaleString()}
          </div>
          <div className="text-xs text-cream-100/60 mt-2">
            All-in flat rate · {isLongDistance ? "Dedicated truck, no shared loads" : "Includes fuel, tolls, blankets & insurance"}
          </div>
        </div>

        {/* Crew snapshot */}
        <div className="px-6 py-4 border-b border-white/10 grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="font-display text-xl font-bold text-white">{crewSize}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-cream-100/50 font-semibold mt-0.5">Movers</div>
          </div>
          <div className="border-x border-white/10">
            <div className="font-display text-xl font-bold text-white">{estimatedHours}h</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-cream-100/50 font-semibold mt-0.5">Est. time</div>
          </div>
          <div>
            <div className="font-display text-xl font-bold text-white">{truckSize}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-cream-100/50 font-semibold mt-0.5">Truck</div>
          </div>
        </div>

        {/* Line items */}
        <div className="px-6 py-5">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-cream-100/50 mb-3">
            Cost breakdown
          </div>
          <ul className="space-y-2.5">
            {lineItems.map((item, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="text-cream-100/90 font-medium">{item.label}</div>
                  {item.meta && (
                    <div className="text-[11px] text-cream-100/45 mt-0.5">{item.meta}</div>
                  )}
                </div>
                <div className={`tabular-nums font-semibold ${item.amount < 0 ? "text-emerald-300" : "text-white"}`}>
                  {item.amount < 0 ? "−" : ""}${Math.abs(item.amount).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-5 pt-4 border-t border-white/10 space-y-2 text-sm">
            <div className="flex items-center justify-between text-cream-100/70">
              <span>Subtotal</span>
              <span className="tabular-nums">${subtotal.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-cream-100/70">
              <span>Tax &amp; fees (8.875%)</span>
              <span className="tabular-nums">${tax.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <span className="font-display font-bold text-white">Total</span>
              <span className="font-display font-bold text-white text-lg tabular-nums">
                ${total.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Deposit */}
        <div className="px-6 py-4 bg-white/[0.03] border-t border-white/10">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-sm">
              <i className="ri-secure-payment-line text-brand-300"></i>
              <span className="font-semibold text-white">Deposit to lock date</span>
            </div>
            <div className="font-display font-bold text-brand-300 tabular-nums">
              ${deposit}
            </div>
          </div>
          <div className="text-[11px] text-cream-100/50 leading-relaxed">
            Refundable up to 7 days before. Remaining balance due on move day.
          </div>
        </div>
      </div>

      {/* Trust ticks */}
      <div className="mt-5 space-y-2.5 text-xs text-ink-600">
        {[
          { icon: "ri-shield-check-line", text: "Full-replacement-value insurance included" },
          { icon: "ri-price-tag-3-line", text: "Flat-rate — this number won't change on move day" },
          { icon: "ri-time-line", text: "On-time guarantee: first hour free if we're >30 min late" },
        ].map((t) => (
          <div key={t.text} className="flex items-start gap-2.5">
            <i className={`${t.icon} text-brand-500 mt-0.5`}></i>
            <span>{t.text}</span>
          </div>
        ))}
      </div>

      {status === "submitting" && (
        <div className="mt-5 text-xs text-ink-500 flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>
          Locking in your quote…
        </div>
      )}
    </div>
  );
}